import { setDefaultOptions } from 'date-fns'
import { flatten } from 'flat'
import { useCallback, useEffect, useState } from 'react'

import {
  getCurrentLocale,
  importDateLocale,
  loadMessages,
  persistLocale,
  ReleasedLocale,
} from '../lib/locale'

type UseLocaleInitializationReturn = {
  isReady: boolean
  locale: string
  messages: Record<string, string>
  changeLocale: (next: ReleasedLocale) => Promise<void>
}

async function resolveLocaleData(locale: string): Promise<Record<string, string>> {
  const [dateLocale, localeMessages] = await Promise.all([
    importDateLocale(locale),
    loadMessages(locale),
  ])
  if (dateLocale) {
    setDefaultOptions({ locale: dateLocale })
  }
  const flattenedMessages = flatten(localeMessages)
  const stringMessages: Record<string, string> = {}
  if (flattenedMessages && typeof flattenedMessages === 'object') {
    for (const [key, value] of Object.entries(flattenedMessages)) {
      stringMessages[key] = String(value)
    }
  }
  return stringMessages
}

export function useLocaleInitialization(): UseLocaleInitializationReturn {
  const [isReady, setIsReady] = useState(false)
  const [locale, setLocale] = useState('en')
  const [messages, setMessages] = useState<Record<string, string>>({})

  useEffect(() => {
    const currentLocale = getCurrentLocale()
    setLocale(currentLocale)

    resolveLocaleData(currentLocale)
      .then((stringMessages) => {
        setMessages(stringMessages)
        setIsReady(true)
      })
      .catch((error) => {
        console.error('Failed to initialize locale data:', error)
        setIsReady(true)
      })
  }, [])

  // Switch the app language at runtime: persist the choice and swap the loaded
  // messages in place (no full reload, so in-progress forms keep their state).
  const changeLocale = useCallback(
    async (next: ReleasedLocale) => {
      if (next === locale) return
      persistLocale(next)
      const stringMessages = await resolveLocaleData(next)
      setMessages(stringMessages)
      setLocale(next)
    },
    [locale]
  )

  return {
    isReady,
    locale,
    messages,
    changeLocale,
  }
}
